import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SECTIONS = [
  { id: "hero",    label: "Hero" },
  { id: "about",   label: "About" },
  { id: "fields",  label: "Fields" },
  { id: "concept", label: "Concept" },
  { id: "tech",    label: "Tech" },
  { id: "links",   label: "Links" },
  { id: "contact", label: "Contact" },
];

export function NavDots() {
  const [active, setActive] = useState("hero");
  const [hovered, setHovered] = useState<string | null>(null);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    SECTIONS.forEach(({ id }) => {
      const el = id === "hero"
        ? document.querySelector("section")
        : document.getElementById(id);
      if (!el) return;

      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(id); },
        { threshold: 0.4 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const scrollTo = (id: string) => {
    if (id === "hero") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="fixed right-5 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-4 items-center">
      {SECTIONS.map(({ id, label }) => (
        <div
          key={id}
          className="relative flex items-center justify-end"
          onMouseEnter={() => setHovered(id)}
          onMouseLeave={() => setHovered(null)}
        >
          {/* Label tooltip */}
          <AnimatePresence>
            {hovered === id && (
              <motion.span
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.18 }}
                className="absolute right-7 text-xs tracking-widest uppercase whitespace-nowrap"
                style={{
                  color: "oklch(0.85 0.18 195)",
                  textShadow: "0 0 10px oklch(0.75 0.20 195 / 0.6)",
                }}
              >
                {label}
              </motion.span>
            )}
          </AnimatePresence>

          {/* Dot */}
          <button
            onClick={() => scrollTo(id)}
            aria-label={label}
            className="relative w-5 h-5 flex items-center justify-center group"
          >
            <motion.div
              animate={
                active === id
                  ? { scale: 1, opacity: 1 }
                  : { scale: 0.6, opacity: 0.35 }
              }
              transition={{ duration: 0.3 }}
              className="w-2 h-2 rounded-full"
              style={{
                background: active === id
                  ? "oklch(0.85 0.18 195)"
                  : "oklch(0.65 0.05 240)",
                boxShadow: active === id
                  ? "0 0 8px oklch(0.75 0.20 195 / 0.9), 0 0 16px oklch(0.65 0.18 195 / 0.5)"
                  : "none",
              }}
            />
          </button>
        </div>
      ))}
    </div>
  );
}
